import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { PAYMENT_COLORS } from '@/constants/residents/mobile/paymentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { RNButton } from '@/components';
import { getIsOverdue, getInCredit } from '@/features/payments/common/helpers';

interface MobilePaymentCardProps {
  leaseId: number;
  residentName?: string;
  propertyName?: string;
  unitNumber?: string;
  balanceDue: number;
  autopayEnabled?: boolean;
  isConnection?: boolean;
  paymentsBlocked?: boolean;
  onPress?: () => void;
}

export const MobilePaymentCard: React.FC<MobilePaymentCardProps> = ({
  leaseId,
  residentName = 'Resident',
  propertyName = '',
  unitNumber = '',
  balanceDue,
  autopayEnabled = false,
  isConnection = false,
  paymentsBlocked = false,
  onPress,
}) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  
  const isOverdue = useMemo(() => getIsOverdue(balanceDue), [balanceDue]);
  const inCredit = useMemo(() => getInCredit(balanceDue), [balanceDue]);
  const shouldPay = useMemo(() => {
    return ((balanceDue || 0) > 0 && !inCredit) || isOverdue;
  }, [balanceDue, inCredit, isOverdue]);

  const handleCardPress = () => {
    if (onPress) {
      onPress();
    } else {
      navigation.navigate('PaymentDetail', { leaseId });
    }
  };

  const handleMakePayment = (e: any) => {
    e.stopPropagation();
    navigation.navigate('MakePayment', { leaseId });
  };

  return (
    <TouchableOpacity 
      style={[
        styles.card,
        isOverdue && styles.cardOverdue,
      ]}
      onPress={handleCardPress}
      activeOpacity={0.7}
    >
      {/* Overdue Banner */}
      {isOverdue && (
        <View style={styles.overdueBanner}>
          <Text style={styles.overdueText}>Past Due</Text>
        </View>
      )}

      <View style={styles.cardContent}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.residentName} numberOfLines={1}>
              {residentName}
            </Text>
            <View style={styles.propertyRow}>
              <Text style={styles.propertyText} numberOfLines={1}>
                {propertyName}
                {unitNumber ? ` • ${unitNumber}` : ''}
              </Text>
            </View>
          </View>
          
          {autopayEnabled && (
            <View style={styles.autopayBadge}>
              <CheckIcon />
              <Text style={styles.autopayText}>Autopay</Text>
            </View>
          )}
        </View>

        {/* Balance */}
        <View style={styles.balanceSection}>
          <Text style={[
            styles.balance,
            isOverdue && styles.balanceOverdue,
            inCredit && styles.balanceCredit,
          ]}>
            {formatCurrency(balanceDue)}
          </Text>
          <Text style={styles.balanceLabel}>
            {shouldPay ? 'Balance due' : inCredit ? 'Credit' : 'No balance due'}
          </Text>
        </View>

        {/* Action Button */}
        {!paymentsBlocked && shouldPay && (
          <RNButton
            title="Make Payment"
            onPress={handleMakePayment}
            variant="tertiary"
            style={StyleSheet.flatten([
              styles.paymentButton,
              isOverdue && styles.paymentButtonOverdue,
            ])}
            textStyle={styles.paymentButtonText}
          />
        )}

        {paymentsBlocked && (
          <Text style={styles.blockedText}>
            Payments are currently unavailable
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

const CheckIcon: React.FC = () => {
  const Icon = RESIDENTS_SVG_COMPONENTS.CHECK as any;
  if (!Icon) return null;
  return (
    <View style={styles.checkIconWrap}>
      <Icon width={12} height={12} color={PAYMENT_COLORS.SUCCESS_GREEN} />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: PAYMENT_COLORS.WHITE,
    borderRadius: 12,
    marginHorizontal: 16,
    marginVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
    overflow: 'hidden',
  },
  cardOverdue: {
    borderColor: PAYMENT_COLORS.ERROR_RED,
    borderWidth: 2,
  },
  overdueBanner: {
    backgroundColor: PAYMENT_COLORS.ERROR_RED,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  overdueText: {
    color: PAYMENT_COLORS.WHITE,
    fontSize: 12,
    fontWeight: '600',
  },
  cardContent: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    flex: 1,
    marginRight: 12,
  },
  residentName: {
    fontSize: 17,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  propertyRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  propertyText: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
  },
  autopayBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: PAYMENT_COLORS.SUCCESS_GREEN_LIGHT,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  checkIconWrap: {
    marginRight: 4,
  },
  autopayText: {
    fontSize: 11,
    color: PAYMENT_COLORS.SUCCESS_GREEN,
    fontWeight: '600',
  },
  balanceSection: {
    marginBottom: 12,
  },
  balance: {
    fontSize: 28,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  balanceOverdue: {
    color: PAYMENT_COLORS.ERROR_RED,
  },
  balanceCredit: {
    color: PAYMENT_COLORS.SUCCESS_GREEN,
  },
  balanceLabel: {
    fontSize: 13,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
  },
  paymentButton: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    paddingVertical: 12,
  },
  paymentButtonOverdue: {
    backgroundColor: PAYMENT_COLORS.ERROR_RED,
  },
  paymentButtonText: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
    fontWeight: '600',
  },
  blockedText: {
    fontSize: 13,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

